import { useEffect, useRef, useState } from 'react';
import { useActionsOff } from '../../auth/useRolePreview';
import type { DoorViewModel } from '../../api/adapters';
import { StatusChip } from '../../design-system/components/StatusChip';
import { ConfirmDialog } from '../../design-system/components/ConfirmDialog';
import { RoleGuard } from '../../auth/RoleGuard';
import { audienceFor, type Audience } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import { useDoorCommand, useDoorHealth } from '../../api/hooks/useDoors';
import { DoorOverrideChip, DoorOverridePanel } from './DoorOverridePanel';
import { DoorWriteGatePanel } from './DoorWriteGatePanel';
import { useDoorOverrides } from '../../state/useDoorOverrides';
import { remainingS } from '../../state/door-overrides';
import type { StatusKind } from '../../design-system/tokens';
import { formatDateTime } from '../../utils/date-format';
import { useDismissOnOutside } from '../../shell/use-dismiss-on-outside';
import { ShowWhereLink } from '../../shell/ShowWhereLink';
import { useActiveSite, useSafeSiteId } from '../../state/useActiveSite';
import { useFeedHealth } from '../../api/feed-health';
import { resolveSiteTimeZone } from '../Tasks/site-timezone';
import { doorLastUpdate } from './door-last-update';
import './DeviceDetailDrawers.css';

const DOOR_STATUS_MAP: Record<string, StatusKind> = {
  open: 'active',
  closed: 'idle',
  moving: 'moving',
  fault: 'error',
};

/**
 * Map the upstream numeric `door_type` (rmf_door_msgs/Door.msg) to a human
 * label. The upstream convention is a bit-flag enum, but the SPA only
 * displays the *kind*. We check the common cases in order.
 *   0 = closed (not really a type — most doors default to 0)
 *   double_hinged / double_sliding / hinged / single_hinged / single_sliding
 * are the common labels used by traffic editor exports.
 */
const DOOR_TYPE_LABELS: Record<number, string> = {
  0: 'hinged',
  1: 'sliding',
  2: 'double_hinged',
  3: 'double_sliding',
  4: 'single_hinged',
  5: 'single_sliding',
  6: 'telescope',
  7: 'swing',
};

function doorTypeLabel(t: number | undefined): string {
  if (t === undefined) return '—';
  return DOOR_TYPE_LABELS[t] ?? `type ${t}`;
}

/** Human labels for `CommandRecord.state` (plan §7.3: "show accepted
 *  versus observed completion" rather than treating the POST
 *  response as done). */
const COMMAND_STATE_LABEL: Record<string, string> = {
  received: 'Received',
  authorized: 'Authorized',
  // "Published to worker" named a process; the hotel reads "Sent" (P9).
  published: 'Sent',
  accepted: 'Accepted — awaiting confirmation',
  observed_applied: 'Applied',
  rejected: 'Rejected',
  timed_out: 'Timed out',
  outcome_unknown: 'Outcome unknown',
};

/**
 * "Open by akash@nexsoss.ai (console)" / "Open for phantas_fleet/phantas_01
 * (RMF, for the robot)" / "Close by door_bench_tool". The source is
 * always named, because the whole point of the row is telling a press
 * from a plan. A site reader gets the robot's name and "the system", not
 * the fleet id and "RMF" (review BC-F10, 2026-10-31).
 */
function describeLastCommand(c: NonNullable<DoorViewModel['lastCommand']>, audience: Audience): string {
  const verb = c.requested === 'open' ? 'Open' : 'Close';
  // A worker paired with an api-server too old to send the operator
  // records the console itself as the actor; say so rather than print
  // "by console (console)".
  if (c.source === 'console' && c.actor === 'console') {
    return `${verb} from the console (operator not recorded)`;
  }
  if (c.source === 'console') return `${verb} by ${c.actor} (console)`;
  if (c.source === 'robot') {
    if (audience === 'ops') return `${verb} for ${c.actor} (RMF, for the robot)`;
    const name = c.actor.includes('/') ? c.actor.slice(c.actor.lastIndexOf('/') + 1) : c.actor;
    return `${verb} for ${name} (by the system, for the robot)`;
  }
  return `${verb} by ${c.actor}`;
}

function formatRelativeTime(iso: string | undefined, now: number): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const delta = Math.max(0, Math.floor((now - t) / 1000));
  if (delta < 5) return 'just now';
  if (delta < 60) return `${delta}s ago`;
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86400)}d ago`;
}

export function DoorDetailDrawer({
  door,
  onClose,
}: {
  door: DoorViewModel;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLElement>(null);
  useDismissOnOutside(panelRef, onClose);
  // Who reads the engineering lines below (review BC-F10): ops keep the
  // adapter and RMF words, a site reader gets what they mean for robots.
  const audience = audienceFor(useAuth().roles);

  // Per-door health, read here as well as in the table. Both surfaces
  // read the same hook for the reason the write-gate panel does: the
  // panel an operator sees while looking at one door must not disagree
  // with the row they were just looking at.
  const health = useDoorHealth().byDoor?.get(door.name);

  const request = useDoorCommand();
  // "View as role": Open/Close stay drawn and are switched off.
  const actionsOff = useActionsOff();
  const [pendingMode, setPendingMode] = useState<'open' | 'close' | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // This console's own record of an override it declared for this
  // door, if any. There is no projection for one -- see
  // `state/door-overrides.ts` -- so the chip below is scoped to what
  // this browser sent, and the panel says so.
  const { byDoor, now: overrideNow } = useDoorOverrides();
  const override = byDoor.get(door.name) ?? null;

  useEffect(() => {
    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, []);

  // Last Update: the worker's stamp as a time of day on the site's clock,
  // or "not current" -- never an age measured against this device's
  // clock (`door-last-update.ts`).
  const siteId = useSafeSiteId();
  const { sites } = useActiveSite();
  const feed = useFeedHealth(siteId);
  const lastUpdate = doorLastUpdate({
    lastSeen: door.lastSeen,
    hasLiveState: door.hasLiveState,
    feedClamped: feed.clamped,
    nowMs: now,
    clock: resolveSiteTimeZone(sites, siteId),
  });

  // BOTH buttons, not just the one the state would have disabled. The
  // existing per-button rules ("already open", "already closed") key on
  // `door.state`, and for a registry-only door that field is 'unknown'
  // — which matches neither rule, so both buttons would have been
  // enabled on the one door in the list that certainly cannot answer.
  // A command sent to it goes to an adapter that is not there.
  const noLiveState = !door.hasLiveState;
  const noCommandReason = noLiveState
    ? audience === 'ops'
      ? 'Nothing is publishing state for this door, so the console cannot command it or observe the result. Check its adapter on the edge box.'
      : "Nothing is reporting this door's state, so the console can't open or close it or see the result. If it stays like this, call Nexsoss."
    : undefined;

  return (
    <aside ref={panelRef} className="nx-device-drawer" role="dialog" aria-label={`Door ${door.name} details`}>
      <header className="nx-device-drawer__head">
        <h2>{door.name}</h2>
        {/* A link to the Overview, pointed at this door; never a drawer. */}
        <ShowWhereLink focus={{ kind: 'door', doorName: door.name }} />
        <button type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="nx-device-drawer__body">
        <div className="nx-device-drawer__row">
          <span>State</span>
          {/* "no data" and "unknown" are two different sentences and
              this drawer has to say the right one. A door whose adapter
              is not publishing (registry-only) reported nothing; a door
              reporting MODE_UNKNOWN reported that it does not know.
              Rendering the first as the second is how `L4_SLL_Door`'s
              dead MEGAZO login could have been mistaken for a door
              having a bad day. */}
          <span>
            {door.hasLiveState ? (
              <StatusChip status={DOOR_STATUS_MAP[door.state] ?? 'offline'} label={door.state} />
            ) : (
              <StatusChip status="offline" label="no data" />
            )}
            {/* Beside the state, not instead of it. While an override
                holds, the mux SUPPRESSES the door's own reports and
                synthesizes MODE_OPEN, so "open" here may be the
                override talking rather than the door -- and an
                operator has to be able to tell those apart. */}
            {override && <DoorOverrideChip remaining={remainingS(override, overrideNow)} />}
          </span>
        </div>
        {!door.hasLiveState && (
          <p className="nx-device-drawer__sub" data-testid="door-no-live-state">
            {audience === 'ops'
              ? 'This door is in the site’s registry — the building map knows it exists — but nothing is publishing state for it. Check the door adapter on the edge box; until it publishes, the fields below are the registry’s and no command can be sent from here.'
              : 'The building map knows this door, but nothing is reporting its state. Until it does, the fields below come from the map and the console can’t open or close it. If it stays like this, call Nexsoss.'}
          </p>
        )}
        {health && (
          <>
            {/* THE SEVEN REASONS, SEPARATED. `state` above is
                `current_mode`, one enum on which the adapter reports
                `offline` for a door missing from the vendor inventory,
                a dead vendor link, a controller whose own `status` says
                offline, a stale sample, a door never sampled, writes
                being disarmed, or shadow mode. An operator could not
                tell those apart and they need opposite actions. The
                three rows below are the ones that answer it.

                IN THE DRAWER ONLY, deliberately. A LIST row reading
                `offline` and `open` at once is read as a bug by anyone
                who has not been told the difference; here there is room
                to say which is which. */}
            <div className="nx-device-drawer__row">
              <span>Controller</span>
              <span data-testid="door-controller-state">
                {health.online === true ? (
                  // `active`, not a bespoke "online": StatusChip's
                  // vocabulary is fixed and adding to it would rebuild
                  // all three consoles for one chip.
                  <StatusChip status="active" label="online" />
                ) : health.online === false ? (
                  <StatusChip status="offline" label="offline" />
                ) : (
                  <StatusChip status="offline" label="unknown" />
                )}
              </span>
            </div>
            {health.observed !== health.published && (
              <p className="nx-device-drawer__sub" data-testid="door-observed-differs">
                {audience === 'ops' ? (
                  <>
                    The door itself reads <strong>{health.observed}</strong>, but RMF is told{' '}
                    <strong>{health.published}</strong>. That is not a fault on its own — the
                    adapter reports offline whenever it cannot be trusted to hold the door, which
                    includes being disarmed. A robot will not be routed through it until the two
                    agree.
                  </>
                ) : (
                  <>
                    The door itself reads <strong>{health.observed}</strong>, but the robots are told{' '}
                    <strong>{health.published}</strong>. That is not a fault on its own: the robots
                    are told a door is offline whenever it can&apos;t be trusted to hold, which
                    includes while door control is switched off. No robot is sent through it until
                    the two agree.
                  </>
                )}
              </p>
            )}
            <div className="nx-device-drawer__row">
              <span>Last reading</span>
              <span data-testid="door-sample-age">
                {health.sample_age_ms == null
                  ? 'never sampled'
                  : `${Math.round(health.sample_age_ms / 1000)}s ago${
                      health.sample_source ? ` (${health.sample_source})` : ''
                    }`}
              </span>
            </div>
            {health.stale && (
              <p className="nx-device-drawer__sub" data-testid="door-health-stale">
                {audience === 'ops' ? 'The adapter has not published health for' : 'Nothing has been heard from this door for'}{' '}
                {health.age_ms == null ? 'some time' : `${Math.round(health.age_ms / 1000)}s`}, so
                the controller state above is unknown rather than offline — we have stopped
                hearing, which is not the same as the controller having died.
              </p>
            )}
            {health.command_failed && (
              <p className="nx-device-drawer__sub" data-testid="door-command-failed">
                A recent <strong>{String(health.command_failed.mode ?? 'command')}</strong> did not
                take: the door still read{' '}
                <strong>{String(health.command_failed.observed ?? 'unchanged')}</strong> after{' '}
                {String(health.command_failed.waited_s ?? '?')}s. It was not retried.
              </p>
            )}
          </>
        )}
        <div className="nx-device-drawer__row">
          <span>Level</span>
          <span>{door.level || '—'}</span>
        </div>
        <div className="nx-device-drawer__row">
          <span>Type</span>
          <span data-testid="door-type">{doorTypeLabel(door.doorType)}</span>
        </div>
        {door.v1 && door.v2 && (
          <div className="nx-device-drawer__row">
            <span>Geometry</span>
            <span>
              ({door.v1.x.toFixed(1)}, {door.v1.y.toFixed(1)}) → ({door.v2.x.toFixed(1)},{' '}
              {door.v2.y.toFixed(1)})
            </span>
          </div>
        )}
        <div className="nx-device-drawer__row">
          <span>Last Update</span>
          <span
            data-testid="door-last-update"
            data-current={lastUpdate.current ? 'true' : 'false'}
            className={lastUpdate.current ? undefined : 'nx-device-drawer__sub'}
          >
            {lastUpdate.text}
          </span>
        </div>
        {/* Only for a door something is publishing state for. A
            registry-only row has no state key to carry a command, so
            "none seen" there would assert a fact the console cannot
            know -- the worker may well have recorded one. */}
        {door.hasLiveState && (
        <div className="nx-device-drawer__row">
          <span>Last Command</span>
          <span data-testid="door-last-command">
            {door.lastCommand ? (
              <>
                {describeLastCommand(door.lastCommand, audience)}
                <span className="nx-device-drawer__sub">
                  {' '}
                  ({formatRelativeTime(new Date(door.lastCommand.atMs).toISOString(), now)},{' '}
                  {formatDateTime(new Date(door.lastCommand.atMs).toISOString())})
                </span>
              </>
            ) : (
              // Honest, and load-bearing: a door that reads Open here with
              // no command was opened by nobody on this platform -- the
              // vendor controller, a badge, a hand.
              'None seen by the platform'
            )}
          </span>
        </div>
        )}

        <RoleGuard
          allowedRoles={['nexsoss:platform_admin', 'nexsoss:site_supervisor', 'nexsoss:service_operator']}
        >
          <div className="nx-device-drawer__actions">
            <button
              type="button"
              disabled={
                actionsOff ||
                noLiveState || request.isPending || door.state === 'open' || door.state === 'moving'
              }
              title={noCommandReason}
              onClick={() => setPendingMode('open')}
              data-testid="door-open-btn"
            >
              Open
            </button>
            <button
              type="button"
              disabled={
                actionsOff ||
                noLiveState ||
                request.isPending ||
                door.state === 'closed' ||
                door.state === 'moving'
              }
              title={noCommandReason}
              onClick={() => setPendingMode('close')}
              data-testid="door-close-btn"
            >
              Close
            </button>
            {request.isError && (
              <span className="nx-device-drawer__error" role="alert">
                {String((request.error as Error)?.message ?? 'Request failed')}
              </span>
            )}
            {request.data && (
              <span className="nx-device-drawer__sub" data-testid="door-command-state">
                {COMMAND_STATE_LABEL[request.data.command.state] ?? request.data.command.state}
              </span>
            )}
          </div>
        </RoleGuard>

        {/* THE SAME COMPONENT the Doors tab renders above its table,
            over the same single query, filtered to the adapter that
            owns this door. Not a second, smaller reading of arm state:
            two readings of one flag in one screen is the bug the panel
            exists to prevent, and it is why the lift drawer embeds its
            panel rather than re-deriving one.

            It is here because "why did Open do nothing" is a question
            asked in front of one door. A disarmed adapter answers every
            Open by refusing at the adapter and logging there, so
            without this the drawer's own error area stays empty and the
            door simply never moves. */}
        <DoorWriteGatePanel door={door.name} />

        <DoorOverridePanel doorName={door.name} hasLiveState={door.hasLiveState} />
      </div>

      <ConfirmDialog
        open={pendingMode !== null}
        title={pendingMode === 'open' ? 'Open door?' : 'Close door?'}
        message={
          pendingMode === 'open'
            ? `Confirm opening ${door.name}. Pedestrians and robots in the doorway may be affected.`
            : `Confirm closing ${door.name}.`
        }
        confirmLabel={pendingMode === 'open' ? 'Open' : 'Close'}
        cancelLabel="Cancel"
        variant={pendingMode === 'open' ? 'warning' : 'primary'}
        onConfirm={() => {
          // Re-checked here, not only on the buttons. The dialog can sit
          // open across a live update, and a door whose adapter dies in
          // that window becomes a registry-only row underneath a drawer
          // that is still mounted -- both screens key it on the name, so
          // the prop changes and nothing remounts. Without this, the one
          // press the disabled buttons exist to refuse would still be
          // sent, to the door that certainly cannot answer.
          if (pendingMode && door.hasLiveState) {
            request.mutate({ name: door.name, mode: pendingMode });
          }
          setPendingMode(null);
        }}
        onCancel={() => setPendingMode(null)}
      />
    </aside>
  );
}
